import { useCallback, useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import { Typography } from '@getsynapse/design-system';
import useModal from 'Hooks/useModal';
import deleteIcon from 'assets/icons/trash-bin-small.svg';

interface DeleteDashboardModalProps {
  handleDelete: () => void;
  onCloseModal: () => void;
  shouldDisplayModal: boolean;
  dashboardName: string;
  isLoading?: boolean;
}

const DeleteDashboardModal = ({
  handleDelete,
  onCloseModal,
  shouldDisplayModal,
  dashboardName,
  isLoading = false,
}: DeleteDashboardModalProps) => {
  const { Modal, modalProps, openModal, closeModal } = useModal();
  const action = intl.get('DELETE');

  const handleCloseModal = useCallback(() => {
    onCloseModal();
    closeModal();
  }, [closeModal, onCloseModal]);

  const confirmDeleteDashboard = useCallback(() => {
    handleDelete();
    handleCloseModal();
  }, [handleCloseModal, handleDelete]);

  useEffect(() => {
    if (shouldDisplayModal && !modalProps.isOpen) {
      openModal(modalProps);
    }
  }, [modalProps, openModal, shouldDisplayModal]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('DELETE'),
        variant: 'primary',
        color: 'danger',
        onClick: confirmDeleteDashboard,
        'data-testid': 'confirm-button',
        loading: isLoading,
        disabled: isLoading,
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModal,
        'data-testid': 'cancel-button',
        disabled: isLoading,
      },
    ],
    [confirmDeleteDashboard, handleCloseModal, isLoading]
  );

  return (
    <Modal
      {...modalProps}
      title={intl.get('DELETE_DASHBOARD_MODAL.TITLE')}
      titleIcon={{
        name: 'trash-outline',
        className: 'text-error-dark',
        src: deleteIcon,
      }}
      size='small'
      aria-label={intl.get('DELETE_DASHBOARD_MODAL.TITLE', { action })}
      childrenClassName='flex flex-col space-y-6'
      data-testid='delete-dashboard-modal'
      closeModal={handleCloseModal}
      actionButtons={actionButtons}
    >
      <>
        <Typography variant='body' data-testid='delete-caption'>
          {intl.get('DELETE_DASHBOARD_MODAL.CAPTION', {
            name: dashboardName,
          })}
          <br />
          {intl.get('DELETE_DASHBOARD_MODAL.SUB_CAPTION')}
        </Typography>
      </>
    </Modal>
  );
};

export default DeleteDashboardModal;
